import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import {
  SortingDownArrowIcon,
  SortingUpArrowIcon,
  VerticalArrowsIcon,
} from "components/icons";
import type { ReactNode } from "react";
import type { ColumnSort } from "shared";

/** Class the parent's hover rule targets — see `sortIndicatorSx`. */
const SORT_INDICATOR_CLASS = "sort-indicator";

const SORT_ICON: Record<ColumnSort, ReactNode> = {
  natural: <VerticalArrowsIcon sx={{ fontSize: "16px" }} />,
  asc: <SortingUpArrowIcon sx={{ fontSize: "16px" }} />,
  desc: <SortingDownArrowIcon sx={{ fontSize: "16px" }} />,
};

/** What the NEXT click does — the cycle is natural → A-Z → Z-A → natural. */
const nextActionText = (sort: ColumnSort, subject: string): string => {
  if (sort === "natural") return `Sort ${subject} A to Z`;
  if (sort === "asc") return `Sort ${subject} Z to A`;
  return `Reset sorting for ${subject}`;
};

/**
 * Current state, appended to the accessible name. The icon carries this
 * visually; without it a screen-reader user is told what the next click does
 * but never what the list is doing now.
 */
const CURRENT_STATE_TEXT: Record<ColumnSort, string> = {
  natural: "currently unsorted",
  asc: "currently sorted A to Z",
  desc: "currently sorted Z to A",
};

/**
 * Hover rule for the row that CONTAINS a `SortableColumnLabel` — spread into
 * that row's `sx`.
 *
 * It lives on the row, not on the button, because hovering anywhere in a column
 * header should reveal the sort affordance (the rule `TableHeader` applies to
 * its whole `th`). An active sort stays visible regardless, so a sorted column
 * still announces itself once the pointer moves away.
 */
export const sortIndicatorSx = (sort: ColumnSort) => ({
  [`& .${SORT_INDICATOR_CLASS}`]: {
    visibility: sort === "natural" ? "hidden" : "visible",
  },
  [`&:hover .${SORT_INDICATOR_CLASS}`]: { visibility: "visible" },
});

interface SortableColumnLabelProps {
  /** Visible column heading, e.g. "Categories". */
  label: string;
  sort: ColumnSort;
  /** Advances the cycle natural → asc → desc → natural. */
  onToggle: () => void;
  /**
   * Nothing in this list to sort. Note this only disables the control while the
   * list is UNSORTED — a sorted-but-empty list keeps its reset reachable, or a
   * filter that matches nothing would trap the user in the current order.
   */
  isEmpty?: boolean;
  /**
   * Overrides `label` in the accessible name where the heading alone is
   * ambiguous — the Service Status screen renders two "Status" columns, one per
   * group, and passes "default statuses" / "custom statuses".
   */
  scope?: string;
}

/**
 * A column heading that sorts its list, cycling unsorted → A-Z → Z-A.
 *
 * For LIST screens that aren't tables — `table-v2`'s `TableHeader` already owns
 * the same affordance for real tables, and this deliberately matches its icons
 * and hover rule so a sorted column reads identically either way. Callers keep
 * their own header row (Service Categories has a flat bar, Service Status welds
 * one to an accordion card); only the control is shared.
 *
 * The parent row must spread `sortIndicatorSx(sort)` for the hover reveal.
 */
export const SortableColumnLabel = ({
  label,
  sort,
  onToggle,
  isEmpty = false,
  scope,
}: SortableColumnLabelProps) => {
  const theme = useTheme();

  const subject = scope ?? label.toLowerCase();
  // Sorting nothing is a no-op that still moves the icon, which reads as the
  // click having been swallowed — but see `isEmpty`: an applied sort stays
  // resettable even with no rows.
  const isInert = isEmpty && sort === "natural";

  return (
    <ButtonBase
      onClick={onToggle}
      disabled={isInert}
      disableRipple
      aria-label={`${nextActionText(sort, subject)}, ${CURRENT_STATE_TEXT[sort]}`}
      sx={{
        gap: "8px",
        px: "4px",
        mx: "-4px",
        borderRadius: "4px",
        color: theme.palette.lightBg.high,
        justifyContent: "center",
        alignItems: "center",
        // The header stays legible when there is nothing to sort — a greyed-out
        // column label reads as a disabled column, not a disabled control.
        "&.Mui-disabled": { color: theme.palette.lightBg.high },
        // ButtonBase clears the UA outline, so focus has to be drawn back in.
        "&.Mui-focusVisible": {
          outline: `2px solid ${theme.palette.purple.main}`,
          outlineOffset: "2px",
        },
      }}
    >
      <Typography variant="caption" sx={{ color: "inherit" }}>
        {label}
      </Typography>
      {/* `color` reaches the NEUTRAL icon only — `VerticalArrowsIcon` draws in
          `currentColor`, while the two active-sort icons carry their own purple
          fill, exactly as they do in `TableHeader`. */}
      <Box
        className={SORT_INDICATOR_CLASS}
        sx={{ display: "flex", mt: "4px", color: theme.palette.lightBg.medium }}
      >
        {SORT_ICON[sort]}
      </Box>
    </ButtonBase>
  );
};
